/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  LiteLLMInstanceDiagnostics,
  LiteLLMInstanceDiagnostics$inboundSchema,
} from "./litellminstancediagnostics.js";
import { ProjectEntry, ProjectEntry$inboundSchema } from "./projectentry.js";

/**
 * How LiteLLM behaves when Speakeasy cannot evaluate a request.
 */
export const LiteLLMInstanceFailurePosture = {
  FailClosed: "fail_closed",
  FailOpen: "fail_open",
} as const;
/**
 * How LiteLLM behaves when Speakeasy cannot evaluate a request.
 */
export type LiteLLMInstanceFailurePosture = ClosedEnum<
  typeof LiteLLMInstanceFailurePosture
>;

/**
 * A provisioned LiteLLM integration and its project-bound ingestion credential metadata.
 */
export type LiteLLMInstance = {
  active: boolean;
  createdAt: Date;
  createdByUserId: string;
  /**
   * Health and identity-attribution diagnostics for a LiteLLM integration. No prompt, credential, or identity values are returned.
   */
  diagnostics: LiteLLMInstanceDiagnostics;
  /**
   * How LiteLLM behaves when Speakeasy cannot evaluate a request.
   */
  failurePosture: LiteLLMInstanceFailurePosture;
  id: string;
  keyPrefix: string;
  lastUsedAt?: Date | undefined;
  name: string;
  organizationId: string;
  project: ProjectEntry;
  updatedAt: Date;
};

/** @internal */
export const LiteLLMInstanceFailurePosture$inboundSchema: z.ZodMiniEnum<
  typeof LiteLLMInstanceFailurePosture
> = z.enum(LiteLLMInstanceFailurePosture);

/** @internal */
export const LiteLLMInstance$inboundSchema: z.ZodMiniType<
  LiteLLMInstance,
  unknown
> = z.pipe(
  z.object({
    active: z.boolean(),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    created_by_user_id: z.string(),
    diagnostics: LiteLLMInstanceDiagnostics$inboundSchema,
    failure_posture: LiteLLMInstanceFailurePosture$inboundSchema,
    id: z.string(),
    key_prefix: z.string(),
    last_used_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    name: z.string(),
    organization_id: z.string(),
    project: ProjectEntry$inboundSchema,
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "created_at": "createdAt",
      "created_by_user_id": "createdByUserId",
      "failure_posture": "failurePosture",
      "key_prefix": "keyPrefix",
      "last_used_at": "lastUsedAt",
      "organization_id": "organizationId",
      "updated_at": "updatedAt",
    });
  }),
);

export function liteLLMInstanceFromJSON(
  jsonString: string,
): SafeParseResult<LiteLLMInstance, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => LiteLLMInstance$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'LiteLLMInstance' from JSON`,
  );
}
